import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Building2, Search, Shield, ArrowRight, Star, Heart, Globe,
  Headset, ShieldCheck, Lock, MapPin, CreditCard, Users,
  Calendar, Home, Key, Wifi, Coffee, Sun, Umbrella, Phone,
  CheckCircle2, Award, AlertTriangle, Eye
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api')) return `${BACKEND_URL}${url}`;
  return url;
};

const DEFAULT_HERO = "https://images.pexels.com/photos/19737883/pexels-photo-19737883.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const ICON_MAP = {
  building: Building2, headset: Headset, 'shield-check': ShieldCheck, lock: Lock,
  star: Star, heart: Heart, globe: Globe, 'map-pin': MapPin, 'credit-card': CreditCard,
  users: Users, calendar: Calendar, home: Home, key: Key, wifi: Wifi, coffee: Coffee,
  sun: Sun, umbrella: Umbrella, phone: Phone, 'check-circle': CheckCircle2, award: Award
};

const FLAG_MAP = { PY: '🇵🇾', AR: '🇦🇷', BR: '🇧🇷', UY: '🇺🇾', CL: '🇨🇱', CO: '🇨🇴', MX: '🇲🇽', US: '🇺🇸' };

function FullPreviewNav({ b, primaryColor, fontHeading }) {
  const logoUrl = resolveUrl(b.logo_url);
  return (
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 px-6 py-3">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          {logoUrl ? (
            <img src={logoUrl} alt="Logo" className="w-10 h-10 rounded-lg object-contain" />
          ) : (
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
              <Building2 className="w-5 h-5 text-white" />
            </div>
          )}
          <span className="font-bold text-lg" style={{ fontFamily: fontHeading, color: primaryColor }}>
            {b.site_name || 'Estadía 365'}
          </span>
        </div>
        <div className="flex items-center gap-4 text-sm text-gray-600">
          <span className="hidden sm:inline cursor-default">Propiedades</span>
          <span className="hidden sm:inline cursor-default">Mapa</span>
          <span className="hidden sm:inline cursor-default">Blog</span>
          <div className="px-4 py-2 rounded-lg text-sm text-white font-medium cursor-default" style={{ backgroundColor: primaryColor }}>
            Iniciar Sesión
          </div>
        </div>
      </div>
    </nav>
  );
}

function FullPreviewHero({ b, primaryColor, secondaryColor, fontHeading, heroImage, heroTitle, heroDesc }) {
  return (
    <section className="relative bg-cover bg-center" style={{ backgroundImage: `url(${heroImage})`, minHeight: '520px' }}>
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative px-6 py-24 flex flex-col items-center text-center justify-center" style={{ minHeight: '520px' }}>
        {b.hero_subtitle && (
          <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-white/80 mb-3">{b.hero_subtitle}</p>
        )}
        <h1 className="font-bold text-white mb-4 leading-tight text-3xl sm:text-4xl lg:text-5xl max-w-3xl" style={{ fontFamily: fontHeading }}>
          {heroTitle}
        </h1>
        {heroDesc && <p className="text-sm sm:text-base text-white/80 max-w-xl mb-8">{heroDesc}</p>}
        <div className="flex items-center bg-white rounded-full px-5 py-3 shadow-lg w-full max-w-lg">
          <Search className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" />
          <span className="text-gray-400 flex-1 text-left text-sm">Buscar destino, propiedad...</span>
          <div className="px-5 py-2 rounded-full text-sm text-white font-medium flex-shrink-0" style={{ backgroundColor: primaryColor }}>
            Buscar
          </div>
        </div>
        <div className="flex items-center gap-2 mt-5 text-white/80 text-sm">
          <Shield className="w-4 h-4" style={{ color: secondaryColor }} /> Pagos seguros y verificados
        </div>
      </div>
    </section>
  );
}

function FullPreviewFeatures({ b, features, primaryColor, secondaryColor, fontHeading }) {
  if (features.length === 0) return null;
  return (
    <section className="px-6 py-16 bg-white">
      <div className="max-w-6xl mx-auto">
        {b.features_label && (
          <p className="text-center text-xs uppercase tracking-[0.2em] mb-2 font-semibold" style={{ color: secondaryColor }}>
            {b.features_label}
          </p>
        )}
        {b.features_title && (
          <h2 className="text-center text-2xl sm:text-3xl font-bold mb-10" style={{ fontFamily: fontHeading, color: '#1C1917' }}>
            {b.features_title}
          </h2>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.slice(0, 4).map((feat, i) => {
            const IconComp = ICON_MAP[feat.icon] || Building2;
            return (
              <div key={feat._key || i} className="p-6 rounded-xl transition-shadow hover:shadow-md" style={{ backgroundColor: `${primaryColor}08` }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${primaryColor}15` }}>
                  <IconComp className="w-6 h-6" style={{ color: primaryColor }} />
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-2" style={{ fontFamily: fontHeading }}>
                  {feat.title || `Feature ${i + 1}`}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed">{feat.description || ''}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FullPreviewDestinations({ b, destinations, secondaryColor, fontHeading }) {
  if (destinations.length === 0) return null;
  return (
    <section className="px-6 py-16" style={{ backgroundColor: '#F5F4F0' }}>
      <div className="max-w-6xl mx-auto">
        {b.destinations_label && (
          <p className="text-center text-xs uppercase tracking-[0.2em] mb-2 font-semibold" style={{ color: secondaryColor }}>
            {b.destinations_label}
          </p>
        )}
        {b.destinations_title && (
          <h2 className="text-center text-2xl sm:text-3xl font-bold mb-10" style={{ fontFamily: fontHeading, color: '#1C1917' }}>
            {b.destinations_title}
          </h2>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {destinations.slice(0, 6).map((dest, i) => (
            <div key={dest._key || i} className="rounded-xl p-6 transition-shadow hover:shadow-md" style={{ backgroundColor: dest.color || '#DCE9E2' }}>
              <span className="text-3xl block mb-2">{FLAG_MAP[dest.code] || dest.code}</span>
              <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily: fontHeading }}>{dest.name}</h3>
              <p className="text-sm text-gray-600 mt-1">{dest.cities}</p>
              {dest.count > 0 && <p className="text-xs text-gray-500 mt-1">{dest.count} propiedades</p>}
              <span className="text-sm font-medium flex items-center gap-1 mt-3" style={{ color: secondaryColor }}>
                Explorar <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FullPreviewCTA({ b, primaryColor, secondaryColor, fontHeading }) {
  return (
    <section className="px-6 py-20" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}DD)` }}>
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3" style={{ fontFamily: fontHeading }}>
          {b.cta_title || '¿Tienes una propiedad?'}
        </h2>
        <p className="text-white/80 mb-6 max-w-lg mx-auto">
          {b.cta_subtitle || 'Unite a nuestra plataforma y llega a miles de viajeros.'}
        </p>
        <div className="inline-block px-8 py-3 rounded-full text-base font-medium cursor-default" style={{ backgroundColor: secondaryColor, color: '#fff' }}>
          {b.cta_button || 'Registrar mi propiedad'}
        </div>
      </div>
    </section>
  );
}

function FullPreviewFooter({ b, primaryColor, fontHeading }) {
  const logoUrl = resolveUrl(b.logo_url);
  return (
    <footer className="px-6 py-10 bg-gray-900">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          {logoUrl ? (
            <img src={logoUrl} alt="Logo" className="w-8 h-8 rounded-lg object-contain" />
          ) : (
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
              <Building2 className="w-4 h-4 text-white" />
            </div>
          )}
          <span className="font-bold text-white" style={{ fontFamily: fontHeading }}>{b.site_name || 'Estadía 365'}</span>
        </div>
        <p className="text-sm text-gray-400">{b.footer_text || b.tagline || 'Tu hogar lejos de casa.'}</p>
        <div className="border-t border-gray-800 mt-6 pt-6 text-xs text-gray-500">
          © {new Date().getFullYear()} {b.site_name || 'Estadía 365'}. Todos los derechos reservados.
        </div>
      </div>
    </footer>
  );
}

export default function BrandingPreviewPublicPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [branding, setBranding] = useState(null);
  const [error, setError] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      setError('No se proporcionó un token de preview.');
      setLoading(false);
      return;
    }
    fetch(`${BACKEND_URL}/api/branding/preview/${token}`)
      .then(res => {
        if (!res.ok) {
          if (res.status === 410) throw new Error('expired');
          throw new Error('not_found');
        }
        return res.json();
      })
      .then(data => {
        setBranding(data.branding);
        setExpiresAt(data.expires_at);
      })
      .catch(err => {
        setError(err.message === 'expired'
          ? 'Este enlace de preview ha expirado.'
          : 'Preview no encontrado. El enlace puede haber sido revocado o es inválido.');
      })
      .finally(() => setLoading(false));
  }, [token]);

  const { primaryColor, secondaryColor, fontHeading, heroImage, heroTitle, heroDesc, features, destinations } = useMemo(() => {
    if (!branding) return { primaryColor: '#264639', secondaryColor: '#C36F4D', fontHeading: 'Outfit', heroImage: DEFAULT_HERO, heroTitle: '', heroDesc: '', features: [], destinations: [] };
    const c = branding.colors || {};
    return {
      primaryColor: c.primary || '#264639',
      secondaryColor: c.secondary || '#C36F4D',
      fontHeading: branding.fonts?.heading || 'Outfit',
      heroImage: branding.hero_image_url ? resolveUrl(branding.hero_image_url) : DEFAULT_HERO,
      heroTitle: (branding.hero_title || '').replace('{properties}', '665').replace('{cities}', '12'),
      heroDesc: (branding.hero_description || '').replace('{properties}', '665').replace('{cities}', '12'),
      features: branding.features || [],
      destinations: branding.destinations || [],
    };
  }, [branding]);

  // Load Google Fonts
  useEffect(() => {
    if (!branding) return;
    const fonts = branding.fonts || {};
    [fonts.heading, fonts.body].filter(Boolean).forEach(fontName => {
      const id = `gfont-${fontName.replace(/\s+/g, '-').toLowerCase()}`;
      if (!document.getElementById(id)) {
        const link = document.createElement('link');
        link.id = id;
        link.rel = 'stylesheet';
        link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@300;400;500;600;700&display=swap`;
        document.head.appendChild(link);
      }
    });
  }, [branding]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-green-800 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-gray-500">Cargando preview...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-amber-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Preview no disponible</h1>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  const b = branding || {};
  const expiresDate = expiresAt ? new Date(expiresAt) : null;

  return (
    <div className="min-h-screen" style={{ fontFamily: `'${branding?.fonts?.body || 'Inter'}', sans-serif` }} data-testid="branding-preview-public">
      {/* Preview banner */}
      <div className="sticky top-0 z-[100] bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-center gap-2 text-sm" data-testid="preview-banner">
        <Eye className="w-4 h-4 text-amber-600 flex-shrink-0" />
        <span className="text-amber-800 font-medium">
          Modo Preview
        </span>
        <span className="text-amber-600 hidden sm:inline">
          — Esta es una vista previa del diseño. Los cambios aún no están publicados.
        </span>
        {expiresDate && (
          <span className="text-amber-500 text-xs hidden md:inline">
            Expira: {expiresDate.toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
      </div>

      <FullPreviewNav b={b} primaryColor={primaryColor} fontHeading={fontHeading} />
      <FullPreviewHero b={b} primaryColor={primaryColor} secondaryColor={secondaryColor} fontHeading={fontHeading} heroImage={heroImage} heroTitle={heroTitle} heroDesc={heroDesc} />
      <FullPreviewFeatures b={b} features={features} primaryColor={primaryColor} secondaryColor={secondaryColor} fontHeading={fontHeading} />
      <FullPreviewDestinations b={b} destinations={destinations} secondaryColor={secondaryColor} fontHeading={fontHeading} />
      <FullPreviewCTA b={b} primaryColor={primaryColor} secondaryColor={secondaryColor} fontHeading={fontHeading} />
      <FullPreviewFooter b={b} primaryColor={primaryColor} fontHeading={fontHeading} />
    </div>
  );
}
